import {
  IN_OPERATOR_VALUE_SEPARATOR,
  RULE_CONDITION_FIELDS,
  RULE_CONDITION_OPERATORS,
  formatVND,
} from "../../constants"

const fieldLabel = (field) => RULE_CONDITION_FIELDS.find((o) => o.value === field)?.label || field

const ISSUE_MESSAGES = {
  UNKNOWN_FIELD: () => "This field isn't recognized, so the rule never matches.",
  FIELD_NOT_AVAILABLE: ({ field }) =>
    `This document type has no ${fieldLabel(field)}, so the rule never matches.`,
  UNKNOWN_OPERATOR: () => "This comparison isn't recognized, so the rule never matches.",
  EMPTY_VALUE: () => "The value is empty, so the rule never matches.",
  INVALID_NUMBER: () => "The value isn't a valid number.",
  COMMA_IN_SINGLE_VALUE: () => "The value contains a comma. Use \"In list\" or remove the comma.",
  CONTRADICTION: ({ field }) =>
    `The conditions on ${fieldLabel(field)} contradict each other, so the rule never matches.`,
  OPERATOR_NOT_PUBLISHABLE: () => "This comparison can't be published for this field.",
  TEXT_VALUE_ON_AMOUNT: () => "Amount is compared as text. It should be a number.",
}

export const PROBLEM_LABELS = {
  NEVER_MATCH: "Never matches",
  NEVER_SELECTED: "Never selected",
  CONDITION_WARNING: "Condition warning",
  HIDES_EXISTING: "Hides a rule",
  OLD_VERSION: "Old version active",
  CONFLICT: "Conflict",
  SHADOWED: "Partly overridden",
  OVERLAP: "Overlap",
}

export const PROBLEM_DESCRIPTIONS = {
  NEVER_MATCH: "The conditions can never all be true, so this rule never applies.",
  NEVER_SELECTED: "Another rule covers every case of this rule and always wins, so this rule never runs.",
  CONDITION_WARNING: "A condition needs fixing, but the rule can still match.",
  HIDES_EXISTING: "This rule covers every case of an active rule and always wins, so that rule stops running.",
  OLD_VERSION: "Another active version of this rule overlaps it.",
  CONFLICT: "Both rules have the same number of conditions, so only version or rule id decides the winner.",
  SHADOWED: "The other rule has more conditions, so it wins where they overlap.",
  OVERLAP: "This rule has more conditions, so it wins where they overlap.",
}

// A blank row is one still being filled in. Sending it would report NEVER_MATCH
// for a rule the admin hasn't finished, so it joins the check once it has a value.
// `rowIndexes` maps the API's conditionIndex back to the row the admin sees.
export const buildOverlapCheckRequest = (rule) => {
  const ruleData = rule?.rule || rule || {}
  const rowIndexes = []
  const conditions = []
  ;(rule?.conditions || []).forEach((c, idx) => {
    const value = String(c.value ?? "").trim()
    if (!value) return
    rowIndexes.push(idx)
    conditions.push({ field: c.field, operator: c.operator, value, valueType: c.valueType })
  })
  return {
    body: { ruleId: ruleData.id || null, entityType: ruleData.entityType, conditions },
    rowIndexes,
  }
}

// Which question the check answers. "saved": conditions match what's stored, so
// only `currentRule` applies; top-level fields would describe a forked version of
// a published rule. "edited": top-level fields are the form, `currentRule` the
// stored rule. "loading": the stored rule isn't known yet, so saved vs edited can't
// be told apart.
export const overlapCheckPlan = (rule, savedRule) => {
  const { body, rowIndexes } = buildOverlapCheckRequest(rule)
  if (!body.ruleId) {
    return { mode: "new", args: body.conditions.length ? body : null, rowIndexes, savedRowIndexes: [] }
  }
  if ((savedRule?.rule || savedRule)?.id !== body.ruleId) {
    return { mode: "loading", args: null, rowIndexes, savedRowIndexes: [] }
  }
  const saved = buildOverlapCheckRequest(savedRule)
  if (JSON.stringify(saved.body.conditions) === JSON.stringify(body.conditions)) {
    return { mode: "saved", args: { ruleId: body.ruleId }, rowIndexes, savedRowIndexes: rowIndexes }
  }
  return { mode: "edited", args: body, rowIndexes, savedRowIndexes: saved.rowIndexes }
}

export const SELECTION_STATUS = {
  OK: { label: "OK", color: "success" },
  PARTIALLY_SHADOWED: { label: "Partly overridden", color: "warning" },
  NEVER_SELECTED: { label: "Never selected", color: "error" },
  NEVER_MATCH: { label: "Never matches", color: "error" },
}

// `currentRule` is computed as if the rule were active and published, so whether
// it really runs has to come from the rule itself.
export const savedRuleState = (savedRule) => {
  const data = savedRule?.rule || savedRule || {}
  if (data.isActive && data.publishedAt) {
    return { title: "Running now", note: null }
  }
  return {
    title: data.publishedAt ? "Saved rule (inactive)" : "Saved draft",
    note: "Not running. This shows how it would behave if it were active.",
  }
}

// Fallback rules overlap every rule, so a row only matters when the draft ties with one.
export const visibleOverlaps = (overlaps = []) =>
  overlaps.filter((o) => !o.isFallback || o.decidedBy !== "CONDITION_COUNT")

export const visibleProblems = (result) => {
  const hidden = (result?.overlaps || []).filter((o) => !visibleOverlaps([o]).length)
  return (result?.problems || [])
    .map((p) => ({ ...p, count: p.count - hidden.filter((o) => o.type === p.type).length }))
    .filter((p) => p.count > 0)
}

const sameText = (a, b) => String(a ?? "").trim() === String(b ?? "").trim()

// `conditionIndex` alone can't name the row: the saved conditions have no guaranteed
// order between requests. It only breaks a tie between identical rows.
const issueRow = (issue, rowIndexes, conditions) => {
  if (issue.conditionIndex == null) return null
  const matches = conditions.reduce((rows, c, idx) => {
    const same =
      sameText(c.field, issue.field) && sameText(c.operator, issue.operator) && sameText(c.value, issue.value)
    return same ? [...rows, idx] : rows
  }, [])
  const mapped = rowIndexes[issue.conditionIndex]
  return matches.includes(mapped) ? mapped : matches[0] ?? null
}

export const issueMessage = (issue, rowIndexes = [], conditions = [], rowLabel = "Condition") => {
  const text = ISSUE_MESSAGES[issue.code]?.(issue) || issue.code
  const row = issueRow(issue, rowIndexes, conditions)
  return row == null ? text : `${rowLabel} ${row + 1}: ${text}`
}

const decisionText = (overlap, result) => {
  if (overlap.decidedBy === "VERSION") {
    return `same number of conditions, decided by version (v${result.version} vs v${overlap.version})`
  }
  if (overlap.decidedBy === "ID") return "same number of conditions and version, decided by rule id"
  return `this rule has ${result.conditionCount} conditions, the other has ${overlap.conditionCount}`
}

const REASON_BY_TYPE = {
  NEVER_SELECTED: "It covers this rule and always wins, so this rule won't run",
  OLD_VERSION: "Another version of this rule is still active. Turn off the one you no longer need in the Rules list",
  HIDES_EXISTING: "This rule covers it and always wins, so that rule won't run",
}

export const overlapReason = (overlap, result) => {
  const decision = decisionText(overlap, result)
  const lead =
    overlap.type === "NEVER_SELECTED" && overlap.sameFamily
      ? "Another version of this rule covers it and always wins. This rule won't run until that version is turned off in the Rules list"
      : REASON_BY_TYPE[overlap.type] ||
        (overlap.winner === "DRAFT" ? "This rule wins where they overlap" : "That rule wins where they overlap")
  const possible = overlap.certainty === "POSSIBLE" ? "They may overlap. " : ""
  return `${possible}${lead} (${decision}).`
}

const conditionText = (c, optionsFor) => {
  const options = optionsFor(c.field)
  const raw = String(c.value ?? "")
  const values = c.operator === "in" ? raw.split(IN_OPERATOR_VALUE_SEPARATOR) : [raw]
  const labels = values
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => {
      if (c.field === "amount" && Number.isFinite(Number(v))) return formatVND(Number(v))
      return options?.find((o) => String(o.value) === v)?.label || v
    })
  const operator = RULE_CONDITION_OPERATORS.find((o) => o.value === c.operator)?.label || c.operator
  return `${operator} ${labels.join(", ")}`
}

// One row per field either rule uses. A field on only one side is "Any" on the
// other, which still overlaps — `shared` marks the fields both rules constrain.
export const conflictRows = (
  draftConditions = [],
  otherConditions = [],
  optionsFor = () => null,
  rowLabel = "Condition"
) => {
  const draft = draftConditions
    .map((c, idx) => ({ ...c, row: idx + 1 }))
    .filter((c) => String(c.value ?? "").trim())
  const fields = [...new Set([...draft, ...otherConditions].map((c) => c.field))]
  return fields.map((field) => {
    const mine = draft.filter((c) => c.field === field)
    const theirs = otherConditions.filter((c) => c.field === field)
    return {
      field,
      label: fieldLabel(field),
      shared: mine.length > 0 && theirs.length > 0,
      draft: mine.map((c) => `${rowLabel} ${c.row}: ${conditionText(c, optionsFor)}`),
      other: theirs.map((c) => conditionText(c, optionsFor)),
    }
  })
}

const amountRange = (conditions = []) => {
  const range = { from: 0, fromInclusive: true, to: Infinity, toInclusive: false }
  const raiseFrom = (v, inclusive) => {
    if (v > range.from || (v === range.from && !inclusive)) {
      range.from = v
      range.fromInclusive = inclusive
    }
  }
  const lowerTo = (v, inclusive) => {
    if (v < range.to || (v === range.to && !inclusive)) {
      range.to = v
      range.toInclusive = inclusive
    }
  }
  for (const c of conditions.filter((x) => x.field === "amount")) {
    const v = Number(c.value)
    if (String(c.value ?? "").trim() === "" || !Number.isFinite(v)) return null
    if (c.operator === ">=" || c.operator === "=") raiseFrom(v, true)
    if (c.operator === ">") raiseFrom(v, false)
    if (c.operator === "<=" || c.operator === "=") lowerTo(v, true)
    if (c.operator === "<") lowerTo(v, false)
    if (![">=", ">", "<=", "<", "="].includes(c.operator)) return null
  }
  return range
}

const inRange = (range, x) =>
  (x > range.from || (x === range.from && range.fromInclusive)) &&
  (x < range.to || (x === range.to && range.toInclusive))

const compactAmount = (n) => {
  if (n >= 1e9) return `${+(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `${+(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `${+(n / 1e3).toFixed(2)}K`
  return String(n)
}

const hasAmountCondition = (conditions = []) => conditions.some((c) => c.field === "amount")

// Lays the draft and every overlapping rule out on one amount axis, one line each.
// Bounds are evenly spaced rather than to scale: next to 1B, a 0–5M range would be
// too thin to see. A line's `bar` is null when its amount isn't one continuous
// range ("in", "!="). Returns null when no rule constrains the amount.
export const amountCompare = (draftConditions, overlaps = []) => {
  const entries = [
    { key: "draft", label: "This rule", severity: null, conditions: draftConditions },
    ...overlaps.map((o) => ({
      key: o.ruleId,
      label: `${o.name} (v${o.version})`,
      severity: o.severity,
      conditions: o.conditions,
    })),
  ]
  if (!entries.some((e) => hasAmountCondition(e.conditions))) return null

  const lines = entries.map((e) => ({ ...e, range: amountRange(e.conditions) }))
  const bounds = [
    ...new Set(lines.flatMap((l) => (l.range ? [l.range.from, l.range.to] : [])).concat(0)),
  ]
    .filter((b) => Number.isFinite(b) && b >= 0)
    .sort((a, b) => a - b)
  const position = (v) => (v === Infinity ? 100 : (bounds.indexOf(v) / bounds.length) * 100)

  return {
    ticks: [
      ...bounds.map((b) => ({ label: compactAmount(b), position: position(b) })),
      { label: "No limit", position: 100 },
    ],
    lines: lines.map(({ key, label, severity, range }) => ({
      key,
      label,
      severity,
      bar: range && { left: position(range.from), right: position(range.to) },
    })),
  }
}

export const amountRangeText = (range) => {
  if (!range || (range.from == null && range.to == null)) return null
  if (range.singlePoint) return `Only at ${formatVND(range.from ?? range.to)}`
  const parts = []
  if (range.from != null) parts.push(`${range.fromInclusive ? "≥" : ">"} ${formatVND(range.from)}`)
  if (range.to != null) parts.push(`${range.toInclusive ? "≤" : "<"} ${formatVND(range.to)}`)
  return `Amount ${parts.join(" and ")}`
}
